import { Button, Tooltip } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import { createStyles, makeStyles } from "@mui/styles";
import ResponsiveDrawer from "../../components/ClippedResponsiveDrawer";
import { AxiosRequestConfig } from "axios";
import { useEffect, useState } from "react";
import { AXIOS_INSTANCE, BASE_URL } from "../../App";
import { useLocalStorage } from "../../utils/LocalStorageHook";
import TableAbstraction from "../../componentUtils/TableAbstraction";
import Header, { tableHeightReducePx } from "./Header";
import TableViewIcon from "@mui/icons-material/TableView";

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

interface ReportingState {
  isLoaded: boolean;
  data: ReportingRow[];
}

interface ReportingRow {
  salesOffice: string;
  masterRoute: string;
  customerNum: string;
  name: string;
  address: string;
  city: string;
  zip: string;
  location: string;
  subtradeChannel: string;
  equipmentNum: string;
  assetNum: string;
  manufacturer: string;
  model: string;
  telemeterModel: string;
  telemeterSerial: string;
  dexType: string;
  dexDeviceId: string;
  lastVisitDt: string;
  salesDays: string;
  accessDays: string;
  consumption: number;
  changerFund: number;
  settlementMethod: number;
  customerErrors: number;
  materialErrors: number;
  dexDataInterval: number;
  dexMapErrors: number;
  dexPrcErrors: number;
  dexFillMissing: number;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    centerDiv: {
      textAlign: "center",
    },
    root: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    rootContainer: {
      [theme.breakpoints.down("md")]: {
        padding: 0,
        paddingTop: theme.spacing(1),
      },
    },
    tokenBlock: {
      width: "500px",
      overflowWrap: "break-word",
    },
    typography: {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(1),
    },
  })
);

async function getReportingData(salesOrg: string, salesOffices: string[], routes: string[], stcs: string[]) {
  const configReporting: AxiosRequestConfig = {
    method: "GET",
    params: {
      clientId: "100",
      salesOrg: salesOrg,
      salesOffices: JSON.stringify(salesOffices),
      routes: JSON.stringify(routes),
      stcs: JSON.stringify(stcs),
    },
    url: `${BASE_URL}/api/reporting/mc`,
  };

  return await AXIOS_INSTANCE(configReporting).then((res: any) => res.data);
}

export default function ConfigurationReporting() {
  const classes = useStyles();
  const [state, setState] = useState<ReportingState>({
    isLoaded: false,
    data: [],
  });

  const [salesOrg] = useLocalStorage("SETTINGS.salesOrg", "");

  const onSelectionChange = (salesOffices: string[], routes: string[], subtradeChannels: string[]) => {
    const sOrg = salesOrg.toString();
    if (salesOffices.length > 0) {
      getReportingData(sOrg, salesOffices, routes, subtradeChannels).then((v) => {
        setState({ isLoaded: true, data: v });
      });
    }
  };

  const DownloadButton = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Machine Configuration Report");

    worksheet.columns = [
      { header: "Customer #", key: "customerNum", width: 20 },
      { header: "Equipment #", key: "equipmentNum", width: 20 },
      { header: "Asset #", key: "assetNum" },
      { header: "Manufacturer", key: "manufacturer", width: 20 },
      { header: "Model", key: "model", width: 10 },
      { header: "Sales Office", key: "salesOffice", width: 10 },
      { header: "Master Route", key: "masterRoute", width: 10 },
      { header: "Name", key: "name", width: 10 },
      { header: "Address", key: "address", width: 10 },
      { header: "City", key: "city", width: 10 },
      { header: "State/Zip", key: "zip", width: 10 },
      { header: "Subtrade Channel", key: "subtradeChannel", width: 10 },
      { header: "Machine Location", key: "location", width: 10 },
      { header: "Last Visit Date", key: "lastVisitDt", width: 10 },
      { header: "Sales Days", key: "salesDays", width: 10 },
      { header: "Access Days", key: "accessDays", width: 10 },
      { header: "Consumption", key: "consumption", width: 10 },
      { header: "Telemeter Model", key: "telemeterModel", width: 10 },
      { header: "Telemeter Serial #", key: "telemeterSerial", width: 10 },
      { header: "Dex Type", key: "dexType", width: 10 },
      { header: "Dex Device Id", key: "dexDeviceId", width: 10 },
      { header: "Changer Fund", key: "changerFund", width: 10 },
      { header: "Settlement Method", key: "settlementMethod", width: 10 },
      { header: "Customer Errors", key: "customerErrors", width: 10 },
      { header: "Material Errors", key: "materialErrors", width: 10 },
      { header: "DEX Data Interval", key: "dexDataInterval", width: 10 },
      { header: "DEX Mapping Errors", key: "dexMapErrors", width: 10 },
      { header: "DEX Pricing Errors", key: "dexPrcErrors", width: 10 },
      { header: "DEX No Fill Key", key: "dexFillMissing", width: 10 },
    ];

    const data = state.data;
    const filterdData: any = [];
    data.forEach((ele: any)=>{
      let dataObject: any = {
        "customerNum": ele.customerNum,
        "equipmentNum": ele.equipmentNum,
        "assetNum": ele.assetNum,
        "manufacturer": ele.manufacturer,
        "model": ele.model,
        "salesOffice": ele.salesOffice,
        "masterRoute": ele.masterRoute,
        "name": ele.name,
        "address": ele.address,
        "city": ele.city,
        "zip": ele.zip,
        "subtradeChannel": ele.subtradeChannel,
        "location": ele.location,
        "lastVisitDt": ele.lastVisitDt,
        "salesDays": ele.salesDays,
        "accessDays": ele.accessDays,
        "consumption": ele.consumption,
        "telemeterModel": ele.telemeterModel,
        "telemeterSerial": ele.telemeterSerial,
        "dexType": ele.dexType,
        "dexDeviceId": ele.dexDeviceId,
        "changerFund": ele.changerFund,
        "settlementMethod": ele.settlementMethod,
        "customerErrors": ele.customerErrors,
        "materialErrors": ele.materialErrors,
        "dexDataInterval": ele.dexDataInterval,
        "dexMapErrors": ele.dexMapErrors,
        "dexPrcErrors": ele.dexPrcErrors,
        "dexFillMissing": ele.dexFillMissing,
      };

      filterdData.push(dataObject);
    }) ;
    
    //inserting the rows starting from porition 2 so that headers would not be override.
    worksheet.insertRows(2, filterdData);
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "ConfigurationReport.xlsx");
  };

  return (
    <ResponsiveDrawer title="Reporting">
      <div className={classes.rootContainer}>
        <Header onSelectionChange={onSelectionChange} />
        <TableAbstraction
          settingsKey="RPT_CNF"
          options={{
            serverSide: false,
            responsive: "vertical",
            selectableRows: "none",
            rowsPerPage: 100,
            sortOrder: [{ name: "customerNum", direction: "asc" }],
            customToolbar: () => {
              return (
                <Tooltip title={`Import Seasonality`}>
                  <Button component="label" style={{ marginRight: 10 }} onClick={DownloadButton}>
                    <TableViewIcon /> Download Data
                  </Button>
                </Tooltip>
              );
            },
            tableHeightReducePx,
          }}
          title="Vending Machine Configuration (BETA)"
          columns={[
            {
              label: "Customer #",
              name: "customerNum",
            },
            {
              label: "Equipment #",
              name: "equipmentNum",
            },
            {
              label: "Asset #",
              name: "assetNum",
            },
            {
              label: "Manufacturer",
              name: "manufacturer",
            },
            {
              label: "Model",
              name: "model",
            },
            {
              label: "Sales Office",
              name: "salesOffice",
            },
            {
              label: "Master Route",
              name: "masterRoute",
            },
            {
              label: "Name",
              name: "name",
            },
            {
              label: "Address",
              name: "address",
            },
            {
              label: "City",
              name: "city",
            },
            {
              label: "State/Zip",
              name: "zip",
            },
            {
              label: "Subtrade Channel",
              name: "subtradeChannel",
            },
            {
              label: "Machine Location",
              name: "location",
            },
            {
              label: "Last Visit Date",
              name: "lastVisitDt",
            },
            {
              label: "Sales Days",
              name: "salesDays",
            },
            {
              label: "Access Days",
              name: "accessDays",
            },
            {
              label: "Consumption",
              name: "consumption",
            },
            {
              label: "Telemeter Model",
              name: "telemeterModel",
            },
            {
              label: "Telemeter Serial #",
              name: "telemeterSerial",
            },
            {
              label: "DEX Type",
              name: "dexType",
            },
            {
              label: "DEX Device Id",
              name: "dexDeviceId",
            },
            {
              label: "Changer Fund",
              name: "changerFund",
            },
            {
              label: "Settlement Method",
              name: "settlementMethod",
            },
            {
              label: "Customer Errors",
              name: "customerErrors",
            },
            {
              label: "Material Errors",
              name: "materialErrors",
            },
            {
              label: "DEX Data Interval",
              name: "dexDataInterval",
            },
            {
              label: "DEX Mapping Errors",
              name: "dexMapErrors",
            },
            {
              label: "DEX Pricing Errors",
              name: "dexPrcErrors",
            },
            {
              label: "DEX No Fill Key",
              name: "dexFillMissing",
            },
          ]}
          data={state.data}
        />
      </div>
    </ResponsiveDrawer>
  );
}
